<template>
  <div class="save-the-date">
    <div
      class="background-flowers"
      :style="{
        background: `url(${saveDateStore.getImages[ImageType.SAVEDATEBACKGROUND]?.url}) no-repeat center center / cover, ${backgroundColor}`,
      }"
    ></div>
    <div class="falling-petals"></div>
    <div class="save-the-date-content">
      <img
        class="save-the-date-content-image"
        :src="saveDateStore.getImages[ImageType.SAVEDATEFRONT]?.url"
        alt=""
      />
      <div class="save-the-date-content-wrapper">
        <h1 class="save-the-date-content-header animated-text">
          SAVE THE DATE
        </h1>
        <HeaderWidget
          class="save-the-date-content-names animated-text"
          :font="textFont"
          :her-name="herName"
          :his-name="hisName"
          header-separator="&"
          :main-color="mainColor"
        />
        <div class="separator"></div>
        <p class="wedding-date animated-text">{{ date }}</p>
        <a
          v-if="placeLink && placeText"
          :href="placeLink"
          class="wedding-date animated-text"
          >{{ placeText }}</a
        >
      </div>
    </div>
  </div>
  <PageFooter class="save-the-date-footer" />
</template>

<script lang="ts" setup>
import { toRef, onMounted } from 'vue';
import PageFooter from '../../../components/clients/common/PageFooter.vue';
import { useStateStore } from '../../../stores/state.store';
import { useSaveDateStore } from '@/stores/saveDate/saveDate.store';
import { ImageType } from 'interface/data/image/type';
import { addOpacityToHex } from '@/helpers/color';
import HeaderWidget from '@/components/clients/common/HeaderWidget.vue';

const stateStore = useStateStore();
const saveDateStore = useSaveDateStore();

const mainColor = stateStore.getColors.mainColor;
const mainColor2 = saveDateStore.getCommonData.mainColor2;
const backgroundColor = addOpacityToHex(
  stateStore.getColors.backgroundColor,
  0.8,
);
const backgroundColor2 = saveDateStore.getCommonData.backgroundColor2;
const herName = saveDateStore.getCommonData.herName;
const hisName = saveDateStore.getCommonData.hisName;
const textFont = saveDateStore.getCommonData.textFont;
const petalData = saveDateStore.getCommonData.petal;
const placeLink = saveDateStore.getCommonData.place;
const date = toRef(() => saveDateStore.getLangAllLangData().date);
const placeText = toRef(() => saveDateStore.getLangAllLangData().place);

onMounted(() => {
  const petalsContainer = document.querySelector('.falling-petals');

  if (saveDateStore.getCommonData.isPetal && petalsContainer) {
    for (let i = 0; i < 50; i++) {
      const petal = document.createElement('div');
      petal.classList.add('petal');

      const borderRadius = `${Math.random() * 50 + 50}% ${Math.random() * 50 + 50}% ${Math.random() * 50 + 50}% 0`;
      const size = Math.random() * 20 + 15;
      const rotationStart = Math.random() * 360;
      const rotationEnd = rotationStart + Math.random() * 360 + 180;
      const translateXEnd = Math.random() * 40 - 20;
      const fallDuration = 10 + Math.random() * 10 + 's';
      const fallDelay = Math.random() * 3 + 's';

      petal.style.width = `${size}px`;
      petal.style.height = `${size + 10}px`;
      petal.style.left = Math.random() * 100 + 'vw';
      petal.style.background = `radial-gradient(circle, ${petalData?.petalColor1} 0%, ${petalData?.petalColor2} 95%)`;
      petal.style.borderRadius = borderRadius;
      petal.style.animationDuration = fallDuration;
      petal.style.animationDelay = fallDelay;

      const keyframes = `
        @keyframes fall-${i} {
          0% {
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate3d(${Math.random()},${Math.random()},${Math.random()},${rotationEnd}deg) translateX(${translateXEnd}vw);
            opacity: 0;
          }
        }
      `;

      const styleSheet = document.createElement('style');
      styleSheet.type = 'text/css';
      styleSheet.innerText = keyframes;
      document.head.appendChild(styleSheet);

      petal.style.animationName = `fall-${i}`;

      petalsContainer.appendChild(petal);
    }
  }
});
</script>

<style lang="scss">
.falling-petals {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;

  .petal {
    position: absolute;
    opacity: 0.9;
    border-radius: 50%;
    animation: fall linear infinite;
    top: -50px;
    pointer-events: none;
  }

  @keyframes fall {
    0% {
      transform: translateY(-10vh) rotate(0deg) translateX(0);
      opacity: 1;
    }

    50% {
      transform: translateY(50vh) rotate3d(1, 2, 1, 60deg) translateX(-15vw);
    }

    100% {
      transform: translateY(100vh) rotate3d(2, -1, 3, 120deg) translateX(-15vw);
      opacity: 0;
    }
  }
}
</style>

<style lang="scss" scoped>
.save-the-date {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: v-bind(backgroundColor2);
  text-align: center;
  min-height: 100vh;
  overflow: hidden;
  font-size: 16px;

  .background-flowers {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.3;
    z-index: 0;
    filter: blur(2px);
  }

  &-content {
    position: relative;
    background: v-bind(backgroundColor);
    padding: 40px;
    border-radius: 20px;
    box-shadow: 0 6px 20px rgb(0 0 0 / 20%);
    z-index: 3;
    min-height: 75vh;
    min-width: 65vw;
    margin: 50px 15px;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    align-items: center;

    &-image {
      border-radius: 10%;
      margin-bottom: 30px;
      box-shadow: 0 4px 10px rgb(0 0 0 / 20%);
      object-fit: cover;

      @media (min-aspect-ratio: 1 / 1) {
        width: 55vh;
        height: 55vh;
      }

      @media (max-aspect-ratio: 1 / 1) {
        width: 60vw;
        height: 60vw;
      }
    }

    .separator {
      width: 40%;
      height: 2px;
      background: v-bind(mainColor);
      margin: 20px auto;
      border-radius: 5px;
    }

    .animated-text {
      animation: fade-in 2s ease-in-out forwards;
    }

    @keyframes fade-in {
      0% {
        opacity: 0;
        transform: scale(0.95);
      }

      100% {
        opacity: 1;
        transform: scale(1);
      }
    }

    &-wrapper {
      width: 100%;
    }

    &-header {
      font-size: 2.8em;
      color: v-bind(mainColor);
      line-height: 1.5em;
      margin-bottom: 20px;
      font-weight: 700;

      @media (max-width: $media-max-phone) {
        font-size: 2.1em;
        line-height: 1.25em;
      }
    }

    &-names {
      font-size: 10px;
      width: 100%;

      @media (max-width: $media-max-phone) {
        font-size: 13px;
      }
    }

    .wedding-date {
      font-size: 32px;
      color: v-bind(mainColor2);

      @media (max-width: $media-max-phone) {
        font-size: 28px;
      }
    }
  }

  &-footer {
    background-color: #000a;
  }
}
</style>
